/**
 * lib/procurementExports.js
 * Excel (.xlsx via `xlsx`) list exports for Goods Inward Notes, Goods
 * Receipt Notes and Purchase Orders. Each takes the rows currently shown
 * on the page (so an active search narrows the export) and writes a
 * workbook with a one-row-per-document sheet plus a one-row-per-line sheet.
 */
import * as XLSX from "xlsx";
import { docCurrency } from "@/lib/currency";

function fmtDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function stamp() {
  return new Date().toISOString().slice(0, 10);
}

function sheet(columns, rows) {
  const ws = XLSX.utils.aoa_to_sheet([
    columns.map((c) => c.header),
    ...rows.map((r) => columns.map((c) => {
      const v = c.get(r);
      return v == null ? "" : v;
    })),
  ]);
  ws["!cols"] = columns.map((c) => ({ wch: c.width || 16 }));
  return ws;
}

function writeBook(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(([name, ws]) => XLSX.utils.book_append_sheet(wb, ws, name));
  XLSX.writeFile(wb, filename);
}

const itemCode = (itemMap, id) => itemMap[id]?.code || "";
const itemName = (itemMap, id) => itemMap[id]?.name || "";
// GIN/GRN lines may be a manual (one-time) item from a Job Work DC — no
// Items master entry, so fall back to the line's own part number/description.
const lineCode = (itemMap, l) => itemCode(itemMap, l.item_id) || l.part_code || "";
const lineName = (itemMap, l) => itemName(itemMap, l.item_id) || l.part_name || "";
const storeName = (storeMap, code) => storeMap[code] || code || "";
const sumLines = (lines, f) => (lines || []).reduce((a, l) => a + Number(l[f] || 0), 0);

const GIN_STATUS = {
  pending_inspection: "Pending QC",
  approved: "Approved",
  partial: "Partial",
  rejected: "Rejected",
};

export function exportGinsExcel({ gins, itemMap = {}, storeMap = {} }) {
  const header = [
    { header: "GIN No.", get: (g) => g.code },
    { header: "Date", get: (g) => fmtDate(g.created_at), width: 14 },
    { header: "Supplier", get: (g) => g.supplier || "Manual", width: 28 },
    { header: "PO No.", get: (g) => g.po_code },
    { header: "Job Work DC", get: (g) => g.dc_code },
    { header: "Invoice No.", get: (g) => g.invoice_no },
    { header: "Invoice Date", get: (g) => fmtDate(g.invoice_date), width: 14 },
    { header: "Vehicle No.", get: (g) => g.vehicle_no },
    { header: "Gate Pass No.", get: (g) => g.gate_pass_no },
    { header: "Lines", get: (g) => (g.lines || []).length, width: 8 },
    { header: "Received Qty", get: (g) => sumLines(g.lines, "received_qty") },
    { header: "Accepted Qty", get: (g) => sumLines(g.lines, "accepted_qty") },
    { header: "Rejected Qty", get: (g) => sumLines(g.lines, "rejected_qty") },
    { header: "Status", get: (g) => GIN_STATUS[g.status] || g.status },
    { header: "GRN No.", get: (g) => g.grn_code },
    { header: "Inspected By", get: (g) => g.inspected_by },
    { header: "Inspected At", get: (g) => fmtDate(g.inspected_at), width: 14 },
    { header: "Created By", get: (g) => g.created_by },
    { header: "Notes", get: (g) => g.notes, width: 36 },
  ];
  const lineRows = (gins || []).flatMap((g) => (g.lines || []).map((l) => ({ g, l })));
  const lines = [
    { header: "GIN No.", get: ({ g }) => g.code },
    { header: "Date", get: ({ g }) => fmtDate(g.created_at), width: 14 },
    { header: "Supplier", get: ({ g }) => g.supplier || "Manual", width: 28 },
    { header: "PO / DC", get: ({ g }) => g.dc_code || g.po_code },
    { header: "Item Code", get: ({ l }) => lineCode(itemMap, l) },
    { header: "Item Name", get: ({ l }) => lineName(itemMap, l), width: 32 },
    { header: "Received Qty", get: ({ l }) => Number(l.received_qty || 0) },
    { header: "Accepted Qty", get: ({ l }) => Number(l.accepted_qty || 0) },
    { header: "Rejected Qty", get: ({ l }) => Number(l.rejected_qty || 0) },
    { header: "Target Store", get: ({ l }) => storeName(storeMap, l.target_store), width: 20 },
    { header: "Remarks", get: ({ l }) => l.remarks, width: 36 },
    { header: "Status", get: ({ g }) => GIN_STATUS[g.status] || g.status },
  ];
  writeBook([["GINs", sheet(header, gins || [])], ["GIN Lines", sheet(lines, lineRows)]],
    `goods_inward_notes_${stamp()}.xlsx`);
}

export function exportGrnsExcel({ grns, itemMap = {}, storeMap = {} }) {
  const header = [
    { header: "GRN No.", get: (g) => g.code },
    { header: "Date", get: (g) => fmtDate(g.created_at), width: 14 },
    { header: "Supplier", get: (g) => g.supplier || "Manual", width: 28 },
    { header: "GIN No.", get: (g) => g.gin_code },
    { header: "PO No.", get: (g) => g.po_code },
    { header: "Lines", get: (g) => (g.lines || []).length, width: 8 },
    { header: "Accepted Qty", get: (g) => sumLines(g.lines, "accepted_qty") },
    { header: "Inspected By", get: (g) => g.inspected_by },
    { header: "Created By", get: (g) => g.created_by },
  ];
  const lineRows = (grns || []).flatMap((g) => (g.lines || []).map((l) => ({ g, l })));
  const lines = [
    { header: "GRN No.", get: ({ g }) => g.code },
    { header: "Date", get: ({ g }) => fmtDate(g.created_at), width: 14 },
    { header: "Supplier", get: ({ g }) => g.supplier || "Manual", width: 28 },
    { header: "GIN No.", get: ({ g }) => g.gin_code },
    { header: "PO No.", get: ({ g }) => g.po_code },
    { header: "Item Code", get: ({ l }) => lineCode(itemMap, l) },
    { header: "Item Name", get: ({ l }) => lineName(itemMap, l), width: 32 },
    { header: "Accepted Qty", get: ({ l }) => Number(l.accepted_qty || 0) },
    { header: "Credited To", get: ({ l }) => storeName(storeMap, l.target_store), width: 20 },
  ];
  writeBook([["GRNs", sheet(header, grns || [])], ["GRN Lines", sheet(lines, lineRows)]],
    `goods_receipt_notes_${stamp()}.xlsx`);
}

const PO_STATUS = { open: "Open", received: "Received", cancelled: "Cancelled" };

export function exportPurchaseOrdersExcel({ pos, itemMap = {} }) {
  const header = [
    { header: "PO No.", get: (p) => p.code },
    { header: "Date", get: (p) => fmtDate(p.created_at), width: 14 },
    { header: "From PR", get: (p) => p.pr_code },
    { header: "Supplier", get: (p) => p.supplier || "Unassigned", width: 28 },
    { header: "Lines", get: (p) => (p.lines || []).length, width: 8 },
    { header: "Total Qty", get: (p) => sumLines(p.lines, "qty") },
    { header: "Currency", get: (p) => docCurrency(p), width: 10 },
    { header: "Total Value", get: (p) => Number(p.total_cost || 0) },
    { header: "Expected Receipt", get: (p) => fmtDate(p.expected_receipt_date), width: 16 },
    { header: "Status", get: (p) => PO_STATUS[p.status] || p.status },
    { header: "Received At", get: (p) => fmtDate(p.received_at), width: 14 },
    { header: "Created By", get: (p) => p.created_by },
  ];
  const lineRows = (pos || []).flatMap((p) => (p.lines || []).map((l) => ({ p, l })));
  const lines = [
    { header: "PO No.", get: ({ p }) => p.code },
    { header: "Date", get: ({ p }) => fmtDate(p.created_at), width: 14 },
    { header: "Supplier", get: ({ p }) => p.supplier || "Unassigned", width: 28 },
    { header: "Item Code", get: ({ l }) => lineCode(itemMap, l) },
    { header: "Item Name", get: ({ l }) => lineName(itemMap, l), width: 32 },
    { header: "Qty", get: ({ l }) => Number(l.qty || 0), width: 10 },
    { header: "Currency", get: ({ p }) => docCurrency(p), width: 10 },
    { header: "Unit Cost", get: ({ l }) => Number(l.unit_cost || 0) },
    { header: "Line Value", get: ({ l }) => Number(l.qty || 0) * Number(l.unit_cost || 0) },
    { header: "Expected Receipt", get: ({ p }) => fmtDate(p.expected_receipt_date), width: 16 },
    { header: "Status", get: ({ p }) => PO_STATUS[p.status] || p.status },
  ];
  writeBook([["Purchase Orders", sheet(header, pos || [])], ["PO Lines", sheet(lines, lineRows)]],
    `purchase_orders_${stamp()}.xlsx`);
}
